/**
 * Orchestrator API service for multi-agent communication.
 *
 * This service communicates with the Multi-Agent Orchestrator at port 8090
 * to discover, select, and interact with different agents.
 *
 * Blueprint: specs/021-multi-agent-orchestrator/contracts/openapi.yaml
 */

// Agent summary returned by /agents endpoint
export interface AgentSummary {
  slug: string;
  name: string;
  description: string;
  status: 'running' | 'stopped' | 'degraded' | 'unknown';
  healthy: boolean;
}

// Agent list response
export interface AgentListResponse {
  agents: AgentSummary[];
  default_agent: string;
}

// Agent details returned by /agents/{slug}
export interface AgentDetails {
  slug: string;
  name: string;
  description: string;
  status: 'running' | 'stopped' | 'degraded' | 'unknown';
  healthy: boolean;
  skills: string[];
  last_health_check: string | null;
}

// Orchestrator health status
export interface OrchestratorHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  agents: {
    slug: string;
    healthy: boolean;
    last_check: string | null;
  }[];
}

// Conversation summary (orchestrator-managed)
export interface OrchestratorConversationSummary {
  id: string;
  title: string | null;
  agent_slug: string;
  message_count: number;
  last_message_at: string | null;
}

// Conversation detail
export interface OrchestratorConversation {
  id: string;
  agent_slug: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  delegations: {
    from_agent: string;
    to_agent: string;
    timestamp: string;
  }[];
}

// Error type
export interface OrchestratorApiError {
  error: string;
  detail?: string;
}

// Get orchestrator base URL - defaults to port 8090
const ORCHESTRATOR_BASE =
  process.env.REACT_APP_ORCHESTRATOR_URL ||
  process.env.REACT_APP_AGENT_URL ||
  'http://localhost:8090';

class OrchestratorApiService {
  private async request<T>(
    endpoint: string,
    options?: RequestInit,
  ): Promise<T> {
    const { getAuthToken } = await import('../context/AuthContext');
    const token = getAuthToken();
    const authHeaders: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const response = await fetch(`${ORCHESTRATOR_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      let errorMessage = 'Request failed';
      try {
        const error: OrchestratorApiError = await response.json();
        errorMessage = error.error || error.detail || errorMessage;
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // =========================================================================
  // Health (T016)
  // =========================================================================

  /**
   * Get orchestrator health status.
   */
  async getHealth(): Promise<OrchestratorHealth> {
    return this.request<OrchestratorHealth>('/health');
  }

  // =========================================================================
  // Agent Discovery (T014, T015)
  // =========================================================================

  /**
   * List all available agents.
   */
  async listAgents(): Promise<AgentListResponse> {
    return this.request<AgentListResponse>('/agents');
  }

  /**
   * Get details for a specific agent.
   * @param slug Agent slug identifier
   */
  async getAgent(slug: string): Promise<AgentDetails> {
    return this.request<AgentDetails>(`/agents/${slug}`);
  }

  /**
   * Get the greeting to show in a brand-new conversation, before any user
   * message. Safe to call for any agent — the backend decides whether one
   * exists (currently Mystique personas only) and returns `welcome_message:
   * null` otherwise.
   * @param slug Agent slug (e.g. "mystique")
   * @param sspSlug SSP persona slug (Mystique only; omit for other agents)
   */
  async getWelcomeMessage(
    slug: string,
    sspSlug?: string,
  ): Promise<{ welcome_message: string | null }> {
    const query = sspSlug ? `?slug=${encodeURIComponent(sspSlug)}` : '';
    return this.request<{ welcome_message: string | null }>(
      `/agents/${slug}/welcome-message${query}`,
    );
  }

  // =========================================================================
  // Conversations (T018)
  // =========================================================================

  /**
   * List conversations for an agent.
   * @param slug Agent slug
   * @param userId User identifier (defaults to 'default')
   */
  async listConversations(
    slug: string,
    userId = 'default',
  ): Promise<OrchestratorConversationSummary[]> {
    return this.request<OrchestratorConversationSummary[]>(
      `/agents/${slug}/conversations?user_id=${encodeURIComponent(userId)}`,
    );
  }

  /**
   * Create a new conversation with an agent.
   * @param slug Agent slug
   * @param userId User identifier
   * @param title Optional conversation title
   */
  async createConversation(
    slug: string,
    userId = 'default',
    title?: string,
  ): Promise<OrchestratorConversation> {
    const params = new URLSearchParams({ user_id: userId });
    if (title) {
      params.set('title', title);
    }
    return this.request<OrchestratorConversation>(
      `/agents/${slug}/conversations?${params.toString()}`,
      { method: 'POST' },
    );
  }

  /**
   * Get a specific conversation.
   * @param slug Agent slug
   * @param conversationId Conversation identifier
   */
  async getConversation(
    slug: string,
    conversationId: string,
  ): Promise<OrchestratorConversation> {
    return this.request<OrchestratorConversation>(
      `/agents/${slug}/conversations/${conversationId}`,
    );
  }

  /**
   * Delete a conversation.
   * @param slug Agent slug
   * @param conversationId Conversation identifier
   */
  async deleteConversation(
    slug: string,
    conversationId: string,
  ): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(
      `/agents/${slug}/conversations/${conversationId}`,
      { method: 'DELETE' },
    );
  }

  // =========================================================================
  // Chat URLs (for transport configuration)
  // =========================================================================

  /**
   * Get the chat endpoint URL for an agent.
   * @param slug Agent slug
   */
  getChatUrl(slug: string): string {
    return `${ORCHESTRATOR_BASE}/agents/${slug}/chat`;
  }

  /**
   * Get the WebSocket URL for an agent.
   * @param slug Agent slug
   * @param sessionId Session identifier
   */
  getWebSocketUrl(slug: string, sessionId: string): string {
    const wsBase = ORCHESTRATOR_BASE.replace(/^http/, 'ws');
    return `${wsBase}/agents/${slug}/ws/${sessionId}`;
  }

  /**
   * Get the default chat endpoint (routes to Dex coordinator).
   */
  getDefaultChatUrl(): string {
    return `${ORCHESTRATOR_BASE}/chat`;
  }

  /**
   * Get the base URL for the orchestrator.
   */
  getBaseUrl(): string {
    return ORCHESTRATOR_BASE;
  }
}

export const orchestratorApi = new OrchestratorApiService();
